import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { parse } from 'qs';

import { getDocumentCategories } from '../../../../actions/userProfile';
import { captureAnalytics } from '../../../../actions/preload';
import dedupeDocumentCategories from '../../../../utils/dedupeDocumentCategories';
import { parseDateFilterParam } from '../../shared/dateFilterParams';
import UploadedAdvancedFilters from './UploadedAdvancedFilters';
import UploadedPrimaryFilters from './UploadedPrimaryFilters';

const parseOptions = { ignoreQueryPrefix: true };
const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY', 'YYYY-MM-DD'];
const SCOPE_KEYS = ['myDepartmentOnly', 'myRecordsOnly'];
const FILTER_KEYS = [
  'contains',
  'category',
  'dob',
  'date_start',
  'date_end',
  'department',
  'document_type',
  ...SCOPE_KEYS,
];

const SORT_OPTIONS = [
  { label: 'Uploaded (newest)', value: 'newest' },
  { label: 'Uploaded (oldest)', value: 'oldest' },
  { label: 'DOB (youngest)', value: 'dob_youngest' },
  { label: 'DOB (oldest)', value: 'dob_oldest' },
];

const ARCHIVED_OPTIONS = [
  { label: 'Active records', value: 'active' },
  { label: 'Archived records', value: 'archived' },
];

const SCOPE_OPTIONS = [
  { label: 'All records', value: 'all' },
  { label: 'My department\'s records', value: 'myDepartmentOnly' },
  { label: 'My records', value: 'myRecordsOnly' },
];

const DOCUMENT_TYPE_OPTIONS = [
  { label: 'Form templates', value: 'form' },
  { label: 'Form submissions', value: 'form_submission' },
];

const normalizeToArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (value === undefined || value === null || value === '') return [];
  if (typeof value === 'string' && value.includes(',')) return value.split(',').filter(Boolean);
  return [String(value)];
};

const filterIsActive = (value) => (
  value !== undefined
  && value !== null
  && value !== ''
  && !(Array.isArray(value) && value.length === 0)
);

const hasActiveSecondaryFilters = (params) => [
  params.date_start,
  params.date_end,
  params.document_type,
].some(filterIsActive);

const UploadedFilterBar = ({
  paramTools,
  getPageDataWithQuery,
  location,
  departments,
  documentCategories,
  getDocumentCategories: fetchDocumentCategories,
}) => {
  const currentParams = useMemo(
    () => parse(location.search || '', parseOptions),
    [location.search],
  );
  const [showMoreFilters, setShowMoreFilters] = useState(
    () => hasActiveSecondaryFilters(currentParams),
  );
  const [searchValue, setSearchValue] = useState(currentParams.contains || '');
  const activeRequestController = useRef(null);
  const [optimisticParams, setOptimisticParams] = useState(currentParams);
  const latestParamsRef = useRef(currentParams);

  useEffect(() => {
    latestParamsRef.current = currentParams;
    setOptimisticParams(currentParams);
    setSearchValue(currentParams.contains || '');
    if (hasActiveSecondaryFilters(currentParams)) setShowMoreFilters(true);
  }, [currentParams]);

  useEffect(() => {
    fetchDocumentCategories();
  }, [fetchDocumentCategories]);

  useEffect(() => () => {
    if (activeRequestController.current) activeRequestController.current.abort();
  }, []);

  const updateQuery = useCallback((updates, source) => {
    const {
      stringify,
      stringifyOptions,
    } = paramTools;
    const previousParams = latestParamsRef.current;
    const nextParams = { ...previousParams, page: 1 };

    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
        delete nextParams[key];
      } else {
        nextParams[key] = value;
      }
    });

    const changedKeys = Object.keys(updates).filter(
      (key) => JSON.stringify(previousParams[key] || null) !== JSON.stringify(nextParams[key] || null),
    );
    if (changedKeys.length === 0) return;

    changedKeys.forEach((key) => {
      captureAnalytics('uploaded_records_filter_changed', {
        filter: key,
        source,
        operation: filterIsActive(nextParams[key]) ? 'set' : 'clear',
      });
    });

    const query = stringify(nextParams, stringifyOptions);
    latestParamsRef.current = nextParams;
    setOptimisticParams(nextParams);

    if (activeRequestController.current) activeRequestController.current.abort();
    const controller = new AbortController();
    activeRequestController.current = controller;
    getPageDataWithQuery(query, controller.signal);
  }, [getPageDataWithQuery, paramTools]);

  const handleSearch = (value) => {
    const trimmedValue = value.trim();
    updateQuery({ contains: trimmedValue || null }, 'search');
  };

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearchValue(value);
    if (!value && optimisticParams.contains) handleSearch('');
  };

  const handleSortChange = (value) => {
    if (value === 'newest') return updateQuery({ sort: null, sort_dir: 'desc' }, 'sort');
    if (value === 'oldest') return updateQuery({ sort: null, sort_dir: 'asc' }, 'sort');
    if (value === 'dob_youngest') return updateQuery({ sort: 'dob', sort_dir: null }, 'sort');
    return updateQuery({ sort: 'dob', sort_dir: 'asc' }, 'sort');
  };

  const handleArchivedChange = (value) => (
    updateQuery({ archived: value === 'archived' ? 'true' : null }, 'archived')
  );

  const handleScopeChange = (value) => {
    const updates = {
      department: null,
      myDepartmentOnly: null,
      myRecordsOnly: null,
    };
    if (value !== 'all') updates[value] = 'true';
    updateQuery(updates, 'scope');
  };

  const handleDepartmentChange = (values) => {
    updateQuery({
      department: values.length ? values : null,
      myDepartmentOnly: null,
      myRecordsOnly: null,
    }, 'department');
  };

  const handleCategoryChange = (value) => updateQuery({ category: value || null }, 'category');

  const handleDobChange = (date) => {
    const dob = date ? date.format('YYYY-MM-DD') : null;
    updateQuery({ dob }, 'dob');
  };

  const handleUploadedDateRangeChange = (dates) => {
    const [startDate, endDate] = dates || [];
    updateQuery({
      date_start: startDate ? startDate.format('YYYY-MM-DD') : null,
      date_end: endDate ? endDate.format('YYYY-MM-DD') : null,
    }, 'uploaded_date_range');
  };

  const handleDocumentTypesChange = (values) => {
    updateQuery({ document_type: values.length ? values : null }, 'document_type');
  };

  const handleToggleMoreFilters = () => {
    const expanded = !showMoreFilters;
    setShowMoreFilters(expanded);
    captureAnalytics('uploaded_records_advanced_filters_toggled', { expanded });
  };

  const handleClearAll = () => {
    const updates = Object.fromEntries(
      [...FILTER_KEYS, 'archived', 'sort', 'sort_dir'].map((key) => [key, null]),
    );
    updateQuery(updates, 'clear_all');
  };

  const sortValue = useMemo(() => {
    if (optimisticParams.sort === 'dob' && optimisticParams.sort_dir === 'asc') return 'dob_oldest';
    if (optimisticParams.sort === 'dob') return 'dob_youngest';
    if (optimisticParams.sort_dir === 'asc') return 'oldest';
    return 'newest';
  }, [optimisticParams.sort, optimisticParams.sort_dir]);

  const scopeValue = useMemo(() => {
    if (optimisticParams.myDepartmentOnly) return 'myDepartmentOnly';
    if (optimisticParams.myRecordsOnly) return 'myRecordsOnly';
    return 'all';
  }, [optimisticParams.myDepartmentOnly, optimisticParams.myRecordsOnly]);

  const departmentOptions = useMemo(
    () => (departments || []).map((department) => ({
      label: department.name,
      value: department.slug,
    })),
    [departments],
  );
  const categoryOptions = useMemo(
    () => dedupeDocumentCategories(documentCategories || []).map((category) => ({
      label: category.emr_category,
      value: category.emr_category,
    })),
    [documentCategories],
  );

  const selectedDepartments = normalizeToArray(optimisticParams.department);
  const selectedDocumentTypes = normalizeToArray(optimisticParams.document_type);
  const activeFilterCount = FILTER_KEYS.filter((key) => filterIsActive(optimisticParams[key])).length
    + (optimisticParams.archived ? 1 : 0)
    + ((optimisticParams.sort || optimisticParams.sort_dir === 'asc') ? 1 : 0);
  const hasSecondaryFilters = hasActiveSecondaryFilters(optimisticParams);

  const primaryHandlers = {
    search: handleSearch,
    searchChange: handleSearchChange,
    dobChange: handleDobChange,
    sortChange: handleSortChange,
    archivedChange: handleArchivedChange,
    scopeChange: handleScopeChange,
    departmentChange: handleDepartmentChange,
    categoryChange: handleCategoryChange,
    toggleMoreFilters: handleToggleMoreFilters,
    clearAll: handleClearAll,
  };

  return (
    <div className="referral-filter-bar uploaded-records-filter-bar">
      <UploadedPrimaryFilters
        optimisticParams={optimisticParams}
        searchValue={searchValue}
        dobValue={optimisticParams.dob ? parseDateFilterParam(optimisticParams.dob) : null}
        dateFormat={dateFormat}
        sortValue={sortValue}
        sortOptions={SORT_OPTIONS}
        archivedOptions={ARCHIVED_OPTIONS}
        scopeValue={scopeValue}
        scopeOptions={SCOPE_OPTIONS}
        departmentOptions={departmentOptions}
        categoryOptions={categoryOptions}
        selectedDepartments={selectedDepartments}
        showMoreFilters={showMoreFilters}
        hasSecondaryFilters={hasSecondaryFilters}
        activeFilterCount={activeFilterCount}
        handlers={primaryHandlers}
      />

      {showMoreFilters && (
        <UploadedAdvancedFilters
          uploadedDateRange={[
            optimisticParams.date_start ? parseDateFilterParam(optimisticParams.date_start) : null,
            optimisticParams.date_end ? parseDateFilterParam(optimisticParams.date_end) : null,
          ]}
          dateFormat={dateFormat}
          documentTypeOptions={DOCUMENT_TYPE_OPTIONS}
          selectedDocumentTypes={selectedDocumentTypes}
          onUploadedDateRangeChange={handleUploadedDateRangeChange}
          onDocumentTypesChange={handleDocumentTypesChange}
        />
      )}
    </div>
  );
};

UploadedFilterBar.defaultProps = {
  departments: [],
  documentCategories: [],
};

UploadedFilterBar.propTypes = {
  paramTools: PropTypes.object.isRequired,
  getPageDataWithQuery: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  departments: PropTypes.array,
  documentCategories: PropTypes.array,
  getDocumentCategories: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({
    departments: state.auth.departments,
    documentCategories: state.userProfile?.allDocumentCategories?.categories
      || state.auth.documentCategories
      || [],
  }),
  { getDocumentCategories },
)(UploadedFilterBar);
